import { computed, ref, watch } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { bulkExportsApi, type BulkExportForm } from '@/api/bulk-exports';

const POLLING_INTERVAL = 3000;

export type BulkExportResult = 'success' | 'warning' | 'error';

// A plain anchor (not axios) lets the browser follow the 302 to the storage URL,
// whose content-disposition names the file.
function triggerDownload(id: number) {
  const link = document.createElement('a');
  link.href = bulkExportsApi.downloadPath(id);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// eslint-disable-next-line max-statements
export function useBulkExport() {
  const exportId = ref<number | null>(null);
  const isPolling = ref(false);
  const lastResult = ref<BulkExportResult | null>(null);
  const lastErrorMessage = ref<string | null>(null);

  const { data: bulkExport, isError } = useQuery({
    queryKey: ['bulkExport', exportId],
    enabled: computed(() => exportId.value !== null),
    queryFn: async () => {
      if (exportId.value === null) return null;
      const result = await bulkExportsApi.show(exportId.value);
      isPolling.value = result.status === 'pending' || result.status === 'processing';

      return result;
    },
    refetchInterval: computed(() => (isPolling.value ? POLLING_INTERVAL : false)),
  });

  function finish(result: BulkExportResult, message: string | null = null) {
    lastResult.value = result;
    lastErrorMessage.value = message;
    exportId.value = null;
    isPolling.value = false;
  }

  watch(() => bulkExport.value?.status, (status) => {
    if (!bulkExport.value || exportId.value === null) return;

    if (status === 'completed') {
      triggerDownload(bulkExport.value.id);
      finish(bulkExport.value.errorMessage ? 'warning' : 'success', bulkExport.value.errorMessage);
    } else if (status === 'failed') {
      finish('error', bulkExport.value.errorMessage);
    }
  });

  watch(isError, (hasError) => {
    if (hasError && exportId.value !== null) finish('error');
  });

  async function startExport(form: BulkExportForm) {
    if (exportId.value !== null) return;

    lastResult.value = null;
    lastErrorMessage.value = null;
    try {
      const created = await bulkExportsApi.create(form);
      isPolling.value = true;
      exportId.value = created.id;
    } catch {
      finish('error');
    }
  }

  const isExporting = computed(() => exportId.value !== null);
  const progressLabel = computed(() => {
    if (!bulkExport.value || exportId.value === null || bulkExport.value.totalCount === 0) return '';

    return `${bulkExport.value.processedCount}/${bulkExport.value.totalCount}`;
  });

  return { startExport, isExporting, progressLabel, lastResult, lastErrorMessage };
}
